import { apiDelete, apiGet, apiPost, apiPut } from "../_apiShim";
import { withSourceEventId } from "@/lib/ai/aiContentQuery";
import { Attachment, AttachmentFile } from "@/types/Attachment";

const BASE_PATH = "/api/v1/template/attachments/";

export async function createTemplateAttachment(
  templateId: string,
  attachment: Partial<Attachment>,
  /**
   * Art. 50 Abs. 2 KI-VO (plan rev. 4.2 J6): "Aus Event importieren" names
   * the SOURCE event, so the server copies that event's guest-content mark
   * onto the event template (attachment names are guest content, J1).
   */
  sourceEventId?: string,
): Promise<string> {
  const response = await apiPost<string>(
    withSourceEventId(`${BASE_PATH}${templateId}`, sourceEventId),
    {
      name: attachment.name,
      assignmentType: attachment.assignmentType,
      sourceType: attachment.sourceType,
      accessType: attachment.accessType,
      targetType: attachment.targetType,
      filters: attachment.filters,
      condition: attachment.condition,
      sortOrder: attachment.sortOrder,
      documentBuilderTemplateIds: attachment.documentBuilderTemplateIds,
      includeCompanionCopies: attachment.includeCompanionCopies,
    },
    {
      "Cache-Control": "no-store",
    },
  );
  if (!response) throw new Error("unknown");
  return response;
}

export async function getTemplateAttachments(
  templateId: string,
): Promise<Attachment[]> {
  const response = await apiGet<Attachment[]>(
    `${BASE_PATH}${templateId}`,
    {
      "Cache-Control": "no-store",
    },
    true,
  );
  return response ?? [];
}

export async function updateTemplateAttachment(
  templateId: string,
  attachmentId: string,
  attachment: Partial<Attachment>,
): Promise<void> {
  await apiPut<void>(
    `${BASE_PATH}${templateId}/${attachmentId}`,
    {
      name: attachment.name,
      assignmentType: attachment.assignmentType,
      sourceType: attachment.sourceType,
      accessType: attachment.accessType,
      targetType: attachment.targetType,
      filters: attachment.filters,
      condition: attachment.condition,
      sortOrder: attachment.sortOrder,
      documentBuilderTemplateIds: attachment.documentBuilderTemplateIds,
      includeCompanionCopies: attachment.includeCompanionCopies,
    },
    {
      "Cache-Control": "no-store",
    },
  );
}

export async function deleteTemplateAttachment(
  templateId: string,
  attachmentId: string,
): Promise<void> {
  await apiDelete<void>(`${BASE_PATH}${templateId}/${attachmentId}`, {
    "Cache-Control": "no-store",
  });
}

// ── File Management (mirrors the event AttachmentClient file endpoints;
//    template file uploads are NEVER guest-scoped, so no guestId) ──

export async function getTemplateAttachment(
  templateId: string,
  attachmentId: string,
): Promise<Attachment> {
  return await apiGet<Attachment>(
    `${BASE_PATH}${templateId}/${attachmentId}`,
    {
      "Cache-Control": "no-store",
    },
    true,
  );
}

export async function presignTemplateFileUpload(
  templateId: string,
  attachmentId: string,
  data: { fileName: string; contentType: string; fileSize: number },
): Promise<{ uploadUrl: string; s3Key: string }> {
  return await apiPost<{ uploadUrl: string; s3Key: string }>(
    `${BASE_PATH}${templateId}/${attachmentId}/files/presign`,
    data,
    { "Cache-Control": "no-store" },
    true,
  );
}

export async function confirmTemplateFileUpload(
  templateId: string,
  attachmentId: string,
  data: { s3Key: string; fileName: string; fileSize: number; contentType: string },
): Promise<AttachmentFile> {
  return await apiPost<AttachmentFile>(
    `${BASE_PATH}${templateId}/${attachmentId}/files/confirm`,
    data,
    { "Cache-Control": "no-store" },
    true,
  );
}

export async function deleteTemplateFile(
  templateId: string,
  attachmentId: string,
  fileId: string,
): Promise<void> {
  await apiDelete<void>(
    `${BASE_PATH}${templateId}/${attachmentId}/files/${fileId}`,
    {
      "Cache-Control": "no-store",
    },
  );
}

export async function getTemplateFileDownloadUrl(
  templateId: string,
  attachmentId: string,
  fileId: string,
): Promise<{ url: string }> {
  return await apiGet<{ url: string }>(
    `${BASE_PATH}${templateId}/${attachmentId}/files/${fileId}/download`,
    {
      "Cache-Control": "no-store",
    },
    true,
  );
}
